import { useState } from 'react'

import type { FieldInputRef, FieldReturnKey } from './FiatFormFieldRow'
import { FiatCountrySheet } from './FiatCountrySheet'
import { FiatPhoneField } from './FiatPhoneField'
import {
  type PhoneCountry,
  countryOnDialCode,
  defaultPhoneCountry,
  leadingDialCode,
  nationalPart,
  toE164,
} from './phoneCountries'
import { useCountrySheet } from './useCountrySheet'

export interface FiatKycPhoneFieldProps {
  /** The form's value: E.164, or empty. */
  value: string
  /** The buyer's region, which the chip opens on when the value names none. */
  defaultCountry?: string
  editable: boolean
  autoFocus: boolean
  returnKey?: FieldReturnKey
  inputRef?: FieldInputRef
  onChangeValue: (value: string) => void
  onBlur: () => void
}

/**
 * The KYC form's phone round, drawn as the session flow draws its phone: the
 * calling code chosen from the country list on the left, the rest typed on
 * the right, and E.164 written to the form. A plain box that asked for the
 * code to be typed left a local number failing the round's own rule.
 */
export function FiatKycPhoneField({
  value,
  defaultCountry,
  editable,
  autoFocus,
  returnKey,
  inputRef,
  onChangeValue,
  onBlur,
}: FiatKycPhoneFieldProps) {
  const [country, setCountry] = useState<PhoneCountry>(() =>
    countryFor(value, defaultCountry),
  )
  const [national, setNational] = useState(() => nationalPart(value))
  const countrySheet = useCountrySheet()

  const changeNational = (next: string) => {
    setNational(next)
    onChangeValue(toE164(country, next))
  }
  const chooseCountry = (next: PhoneCountry) => {
    setCountry(next)
    onChangeValue(toE164(next, national))
    countrySheet.close()
  }

  return (
    <>
      <FiatPhoneField
        country={country}
        national={national}
        isOpen={countrySheet.isOpen}
        editable={editable}
        autoFocus={autoFocus}
        returnKeyType={returnKey?.kind}
        onChangeNational={changeNational}
        onOpenCountries={() => countrySheet.open('PHONE_NUMBER')}
        onSubmitEditing={() => {
          onBlur()
          returnKey?.onPress()
        }}
        onFocus={countrySheet.close}
        onLayoutRef={(node) => {
          if (typeof inputRef === 'function') {
            inputRef(node)
          } else if (inputRef !== undefined) {
            inputRef.current = node
          }
        }}
      />
      <FiatCountrySheet
        sheet={countrySheet.sheet}
        selected={country}
        onSelect={chooseCountry}
      />
    </>
  )
}

/** The chip's country: the one the value's code names, else the buyer's region. */
function countryFor(value: string, defaultCountry: string | undefined) {
  const fallback = defaultPhoneCountry(defaultCountry)
  if (!value.startsWith('+')) {
    return fallback
  }
  const code = leadingDialCode(value.replace(/\D/g, ''))
  return code === undefined ? fallback : countryOnDialCode(code, fallback)
}
